import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Clock3, Globe2, ShieldCheck, XCircle } from "lucide-react";
import { useState } from "react";

const formatDate = (value) => {
  if (!value) return "Not issued";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const expiryTone = (value) => {
  if (!value) return { label: "No active certificate", className: "border-slate-700 bg-slate-950/50 text-slate-500" };
  const days = Math.ceil((new Date(value).getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, className: "border-rose-500/30 bg-rose-500/10 text-rose-300" };
  if (days <= 30) return { label: `${days}d remaining`, className: "border-amber-500/30 bg-amber-500/10 text-amber-300" };
  return { label: `${days}d remaining`, className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" };
};

const statusMeta = {
  issued: { label: "TLS issued", className: "text-emerald-300", icon: CheckCircle2 },
  pending_dns: { label: "Pending DNS", className: "text-amber-300", icon: Clock3 },
  retry: { label: "TLS retry", className: "text-rose-300", icon: AlertTriangle },
  invalid_domain: { label: "Invalid", className: "text-slate-400", icon: XCircle },
  issuing: { label: "Issuing", className: "text-cyan-300", icon: ShieldCheck },
};

const SummaryMetric = ({ label, testId, tone, value }) => (
  <div className="min-w-0">
    <p className="text-[9px] uppercase text-slate-600">{label}</p>
    <p className={`mt-0.5 font-mono text-sm font-semibold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const CertificateStatusPanel = ({ server }) => {
  const [expanded, setExpanded] = useState(false);
  const summary = server.certificate_summary || {};
  const domains = server.certificate_domains || [];
  const expiry = expiryTone(summary.expires_at);

  return (
    <div className="mt-3 border-y border-slate-800 py-3" data-testid={`certificate-status-${server.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-cyan-300" />
            <p className="text-xs font-semibold text-slate-200">DNS & certificate status</p>
          </div>
          <p className="mt-1 text-[10px] text-slate-600" data-testid={`certificate-last-checked-${server.id}`}>
            {summary.last_checked_at ? `Checked ${new Date(summary.last_checked_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Awaiting first reconciliation"}
          </p>
        </div>
        <button type="button" onClick={() => setExpanded((current) => !current)} className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-cyan-300 transition-colors hover:bg-cyan-500/10" data-testid={`certificate-status-toggle-${server.id}`} aria-expanded={expanded}>
          {expanded ? "Hide" : "Details"}{expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <SummaryMetric label="DNS ready" value={summary.dns_ready || 0} tone="text-cyan-300" testId={`certificate-dns-ready-${server.id}`} />
        <SummaryMetric label="TLS issued" value={summary.tls_issued || 0} tone="text-emerald-300" testId={`certificate-tls-issued-${server.id}`} />
        <SummaryMetric label="Pending" value={summary.pending_dns || 0} tone="text-amber-300" testId={`certificate-pending-dns-${server.id}`} />
        <SummaryMetric label="Errors" value={summary.tls_error || 0} tone="text-rose-300" testId={`certificate-tls-error-${server.id}`} />
      </div>

      <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-[11px] ${expiry.className}`} data-testid={`certificate-expiry-${server.id}`}>
        <span className="inline-flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" />Unified certificate: {formatDate(summary.expires_at)}</span>
        <span className="font-mono">{expiry.label}</span>
        {!!summary.invalid && <span className="text-slate-400" data-testid={`certificate-invalid-${server.id}`}>{summary.invalid} invalid</span>}
      </div>

      {expanded && (
        <div className="mt-3 max-h-72 overflow-y-auto border-t border-slate-800" data-testid={`certificate-domain-list-${server.id}`}>
          {domains.length ? domains.map((domain) => {
            const meta = statusMeta[domain.status] || statusMeta.pending_dns;
            const StatusIcon = meta.icon;
            return (
              <div key={domain.id} className="border-b border-slate-800/80 py-2.5 last:border-b-0" data-testid={`certificate-domain-${server.id}-${domain.id}`}>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs text-slate-200" title={domain.domain}>{domain.domain}</p>
                    <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-600"><Globe2 className="h-3 w-3" />{domain.dns_ready ? `Points to ${domain.target_ip}` : `Waiting for ${domain.target_ip || "assigned IP"}`}</p>
                  </div>
                  <span className={`inline-flex flex-none items-center gap-1 text-[10px] font-medium ${meta.className}`} data-testid={`certificate-domain-status-${server.id}-${domain.id}`}><StatusIcon className="h-3 w-3" />{meta.label}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-600">
                  <span>{domain.expires_at ? `Expires ${formatDate(domain.expires_at)}` : "No trusted certificate recorded"}</span>
                  {domain.last_checked_at && <span>{new Date(domain.last_checked_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
                {domain.last_error && <p className="mt-1.5 break-words text-[10px] text-rose-300/80" data-testid={`certificate-domain-error-${server.id}-${domain.id}`}>{domain.last_error}</p>}
              </div>
            );
          }) : <p className="py-4 text-center text-xs text-slate-500">No assigned domains</p>}
        </div>
      )}
    </div>
  );
};